import { useState, useEffect } from 'react'
import { useAuth, useUser } from './lib/auth'
import { OnboardingPage } from './components/OnboardingPage'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

// ============================================================================
// Main App
// ============================================================================

export default function App() {
  const { session, loading } = useAuth()

  if (loading) {
    return (
      <div className="onboarding-wrapper">
        <div className="waveform">
          {[1,2,3,4,5].map(i => <div key={i} className="waveform-bar" />)}
        </div>
      </div>
    )
  }

  return (
    <>
      <Header />
      <main className="main">
        {session ? <Dashboard /> : <LandingPage />}
      </main>
    </>
  )
}

// ============================================================================
// Header
// ============================================================================

function Header() {
  const { session, signOut } = useAuth()

  return (
    <header className="header">
      <div className="header-inner">
        <a href="/" className="logo">
          <Waveform />
          <span className="logo-text">cloop<span>OS</span></span>
        </a>

        <nav style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {session ? (
            <>
              <a href="/api/docs" className="btn btn-ghost">API Docs</a>
              <button className="btn btn-ghost" onClick={() => signOut()}>Sign Out</button>
            </>
          ) : null}
        </nav>
      </div>
    </header>
  )
}

// ============================================================================
// Landing Page
// ============================================================================

function LandingPage() {
  const { sendOtp, verifyOtp } = useAuth()
  const [showSignIn, setShowSignIn] = useState(false)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  function resetModal() { setShowSignIn(false); setStep('email'); setEmail(''); setCode(''); setError(null) }

  async function handleSendCode(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true); setError(null)
    const { error } = await sendOtp(email)
    setLoading(false)
    if (error) setError(error.message)
    else setStep('code')
  }

  async function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true); setError(null)
    const { error } = await verifyOtp(email, code.trim())
    setLoading(false)
    if (error) setError(error.message)
    else resetModal()
  }

  return (
    <>
      <section className="hero">
        <h1 className="hero-title">Memory for AI Agents</h1>
        <p className="hero-description">
          Capture real-world conversations and make them queryable by AI.
          Your agent can finally know what you said in person, not just what you typed.
        </p>
        <div className="hero-actions">
          <button className="btn btn-primary btn-lg" onClick={() => setShowSignIn(true)}>Sign In</button>
          <a href="/api/docs" className="btn btn-secondary btn-lg">View API Docs</a>
        </div>
      </section>

      {showSignIn && (
        <Modal title="Sign In" onClose={resetModal}>
          {step === 'email' ? (
            <form onSubmit={handleSendCode} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {error && <div className="toast toast-error" style={{ justifyContent: 'center' }}>{error}</div>}
              <div>
                <label className="input-label">Email</label>
                <input type="email" className="input" value={email}
                  onChange={e => setEmail(e.target.value)} placeholder="you@clo0p.com" autoFocus required />
              </div>
              <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={loading}>
                {loading ? 'Sending...' : 'Send Code'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyCode} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <p style={{ textAlign: 'center', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                Enter the 6-digit code sent to <strong>{email}</strong>
              </p>
              {error && <div className="toast toast-error" style={{ justifyContent: 'center' }}>{error}</div>}
              <input type="text" className="input" value={code} onChange={e => setCode(e.target.value)}
                placeholder="123456" maxLength={6} autoFocus required
                style={{ textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.3em' }} />
              <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%' }} disabled={loading || code.length < 6}>
                {loading ? 'Verifying...' : 'Sign In'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setStep('email')} style={{ width: '100%' }}>
                Use different email
              </button>
            </form>
          )}
        </Modal>
      )}


      <section className="features-grid">
        <FeatureCard
          icon={<MicIcon />}
          title="Capture Everything"
          description="Upload voice memos, record in browser, or connect via WhatsApp. Every conversation is transcribed and indexed."
        />
        <FeatureCard
          icon={<UsersIcon />}
          title="Know Who Spoke"
          description="Enroll up to 21 speakers. Advanced speaker recognition identifies voices across all your conversations."
        />
        <FeatureCard
          icon={<BotIcon />}
          title="Query via MCP"
          description="Connect to Claude or ChatGPT via MCP. Ask 'What did Sophie say about Q1?' and get instant answers."
        />
      </section>

      <div className="code-block">
        <pre>
          <code>
            <span className="token-keyword">curl</span> -X POST https://cloopos.com/api/v1/agent/query \{'\n'}
            {'  '}-H <span className="token-string">"X-API-Key: cloop_your_key_here"</span> \{'\n'}
            {'  '}-d <span className="token-string">'{`{"query": "What did I discuss with my dentist last week?"}`}'</span>
          </code>
        </pre>
      </div>
    </>
  )
}

function FeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="feature-card">
      <div className="feature-icon">{icon}</div>
      <h3 className="feature-title">{title}</h3>
      <p className="feature-description">{description}</p>
    </div>
  )
}

// ============================================================================
// Dashboard
// ============================================================================

interface Speaker {
  id: string
  name: string
  is_primary: boolean
  sample_count: number
  created_at: string
}

interface Conversation {
  id: string
  title: string | null
  capture_method: string
  start_time: string
  duration_seconds: number | null
  status: string
  summary?: string | null
  topics?: string[] | null
  action_items?: string[] | null
  speakers?: ConversationSpeaker[]
  segments?: Segment[]
}

interface ConversationSpeaker {
  id: string
  name: string
  is_primary: boolean
  confidence: number | null
}

interface Segment {
  id: string
  speaker_id: string | null
  start_offset_ms: number
  end_offset_ms: number
  text: string
  confidence: number | null
  is_verified: boolean
}

interface SearchResult {
  id: string
  title: string | null
  summary: string | null
  start_time: string | null
  duration_seconds: number | null
  relevance_score: number
  matched_segments: Segment[]
}

interface Stats {
  total_conversations: number
  total_minutes: number
  total_words: number
  total_speakers: number
}

function Dashboard() {
  const { user } = useUser()
  const { getToken } = useAuth()

  const [activeModal, setActiveModal] = useState<'record' | 'upload' | 'speaker' | null>(null)
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [needsOnboarding, setNeedsOnboarding] = useState<boolean | null>(null)
  const [speakers, setSpeakers] = useState<Speaker[]>([])
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [trainingSpeaker, setTrainingSpeaker] = useState<Speaker | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[] | null>(null)
  const [searching, setSearching] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  async function fetchData() {
    try {
      const token = await getToken()
      const headers = { Authorization: `Bearer ${token}` }

      const [speakersRes, convRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/v1/speakers`, { headers }),
        fetch(`${API_BASE}/v1/conversations?page_size=10`, { headers }),
        fetch(`${API_BASE}/v1/conversations/stats`, { headers })
      ])

      if (speakersRes.ok) {
        const data = await speakersRes.json()
        setSpeakers(data.items || [])
        const hasPrimary = (data.items || []).some((s: Speaker) => s.is_primary)
        setNeedsOnboarding(!hasPrimary)
      }

      if (convRes.ok) {
        const data = await convRes.json()
        setConversations(data.items || [])
      }

      if (statsRes.ok) {
        const data = await statsRes.json()
        setStats(data)
      }
    } catch (err) {
      console.error('Failed to fetch:', err)
      setNeedsOnboarding(true)
    } finally {
      setLoading(false)
    }
  }

  function showNotification(type: 'success' | 'error', message: string) {
    setNotification({ type, message })
    setTimeout(() => setNotification(null), 5000)
  }

  function handleOnboardingComplete() {
    setNeedsOnboarding(false)
    fetchData()
  }

  function handleUploadComplete() {
    setActiveModal(null)
    showNotification('success', 'Audio uploaded! Processing will complete in a few minutes.')
    fetchData()
  }

  function handleSpeakerAdded() {
    setActiveModal(null)
    showNotification('success', 'Speaker enrolled successfully!')
    fetchData()
  }

  function handleTrainingComplete() {
    setTrainingSpeaker(null)
    showNotification('success', 'Voice training complete! Recognition should improve.')
    fetchData()
  }

  async function handleConversationClick(conversationId: string) {
    setLoadingDetail(true)
    try {
      const token = await getToken()
      const res = await fetch(`${API_BASE}/v1/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setSelectedConversation(data)
      }
    } catch (err) {
      console.error('Failed to fetch conversation:', err)
      showNotification('error', 'Failed to load conversation')
    } finally {
      setLoadingDetail(false)
    }
  }

  function handleBackToList() {
    setSelectedConversation(null)
  }

  async function handleSearch(query: string) {
    if (!query.trim() || query.length < 2) {
      setSearchResults(null)
      return
    }

    setSearching(true)
    try {
      const token = await getToken()
      // Note: semantic=true adds 10-30s Modal cold start latency, disabled for now
      const res = await fetch(`${API_BASE}/v1/conversations/search?q=${encodeURIComponent(query)}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setSearchResults(data)
      }
    } catch (err) {
      console.error('Search failed:', err)
    } finally {
      setSearching(false)
    }
  }

  function clearSearch() {
    setSearchQuery('')
    setSearchResults(null)
  }

  if (loading || needsOnboarding === null) {
    return (
      <div className="onboarding-wrapper">
        <div className="waveform">
          {[1,2,3,4,5].map(i => <div key={i} className="waveform-bar" />)}
        </div>
      </div>
    )
  }

  if (needsOnboarding) {
    return <OnboardingPage onComplete={handleOnboardingComplete} />
  }

  const primarySpeaker = speakers.find(s => s.is_primary)

  // Show conversation detail view if selected
  if (selectedConversation) {
    return (
      <ConversationDetailView
        conversation={selectedConversation}
        speakers={speakers}
        onBack={handleBackToList}
        onRefresh={() => handleConversationClick(selectedConversation.id)}
      />
    )
  }

  return (
    <>
      {notification && (
        <div className={`toast toast-${notification.type}`} style={{ marginBottom: '1.5rem' }}>
          {notification.type === 'success' ? <CheckIcon /> : <AlertIcon />}
          <span>{notification.message}</span>
        </div>
      )}

      {/* Welcome */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>
          Welcome back, {user?.firstName || 'there'}
        </h1>
        <p style={{ color: 'var(--text-muted)' }}>
          Manage your conversations and speakers
        </p>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: '2rem' }}>
        <StatCard label="Conversations" value={stats?.total_conversations ?? conversations.length} />
        <StatCard label="Speakers" value={stats?.total_speakers ?? speakers.length} />
        <StatCard label="Minutes" value={stats?.total_minutes ?? 0} unit="captured" />
        <StatCard label="Words" value={stats?.total_words ?? 0} unit="transcribed" />
      </div>

      {/* Two column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
        {/* Quick Actions */}
        <div className="card">
          <h2 className="section-title" style={{ marginBottom: '1rem' }}>Quick Actions</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <ActionButton icon={<UploadIcon />} onClick={() => setActiveModal('upload')}>
              Upload Audio
            </ActionButton>
            <ActionButton icon={<MicIcon />} onClick={() => setActiveModal('record')}>
              Record Now
            </ActionButton>
            <ActionButton icon={<UserPlusIcon />} onClick={() => setActiveModal('speaker')}>
              Add Speaker
            </ActionButton>
            <ActionButton icon={<KeyIcon />}>
              Get API Key
            </ActionButton>
          </div>
        </div>

        {/* Getting Started */}
        <div className="card">
          <h2 className="section-title" style={{ marginBottom: '1rem' }}>Getting Started</h2>
          <div className="checklist">
            <ChecklistItem done={!!primarySpeaker}>Enroll your voice (primary speaker)</ChecklistItem>
            <ChecklistItem done={conversations.length > 0}>Upload or record your first conversation</ChecklistItem>
            <ChecklistItem done={speakers.length > 1}>Add people you frequently talk to</ChecklistItem>
            <ChecklistItem done={false}>Generate an API key</ChecklistItem>
            <ChecklistItem done={false}>Connect to Claude via MCP</ChecklistItem>
          </div>
        </div>
      </div>

      {/* Speakers */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div className="section-header">
          <h2 className="section-title">Speakers</h2>
          <button className="section-action" onClick={() => setActiveModal('speaker')}>
            + Add Speaker
          </button>
        </div>
        {speakers.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '0.75rem' }}>
            {speakers.map(speaker => (
              <SpeakerCard
                key={speaker.id}
                speaker={speaker}
                onTrain={setTrainingSpeaker}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<UsersIcon />}
            title="No speakers yet"
            text="Add speakers to identify voices in your conversations"
          />
        )}
      </div>

      {/* Conversations Section */}
      <div className="card">
        <div className="section-header">
          <h2 className="section-title">Conversations</h2>
        </div>

        {/* Search */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <SearchIcon style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
              <input
                type="text"
                className="input"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch(searchQuery)}
                style={{ paddingLeft: '2.75rem' }}
              />
            </div>
            <button
              className="btn btn-primary"
              onClick={() => handleSearch(searchQuery)}
              disabled={searching || searchQuery.length < 2}
            >
              {searching ? '...' : 'Search'}
            </button>
            {searchResults && (
              <button className="btn btn-ghost" onClick={clearSearch}>
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Search Results or Recent Conversations */}
        {searchResults ? (
          <div>
            {searchResults.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                No results found for "{searchQuery}"
              </div>
            ) : (
              <>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                  Found {searchResults.length} conversation{searchResults.length !== 1 ? 's' : ''}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {searchResults.map(result => (
                    <SearchResultCard
                      key={result.id}
                      result={result}
                      onClick={() => handleConversationClick(result.id)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : conversations.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {conversations.map(conv => (
              <ConversationItem
                key={conv.id}
                conversation={conv}
                onClick={() => handleConversationClick(conv.id)}
                loading={loadingDetail}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<MicIcon />}
            title="No conversations yet"
            text="Upload an audio file or start recording to begin"
          />
        )}
      </div>

      {/* Modals */}
      {activeModal === 'record' && (
        <Modal title="Record Audio" onClose={() => setActiveModal(null)}>
          <AudioRecorderPanel onComplete={handleUploadComplete} />
        </Modal>
      )}
      {activeModal === 'upload' && (
        <Modal title="Upload Audio" onClose={() => setActiveModal(null)}>
          <AudioUploader onComplete={handleUploadComplete} />
        </Modal>
      )}
      {activeModal === 'speaker' && (
        <Modal title="Add Speaker" onClose={() => setActiveModal(null)}>
          <SpeakerEnroller onComplete={handleSpeakerAdded} />
        </Modal>
      )}
      {trainingSpeaker && (
        <Modal title={`Train ${trainingSpeaker.name}'s Voice`} onClose={() => setTrainingSpeaker(null)}>
          <SpeakerTrainer speaker={trainingSpeaker} onComplete={handleTrainingComplete} />
        </Modal>
      )}
    </>
  )
}

// ============================================================================
// Dashboard Components
// ============================================================================

function StatCard({ label, value, unit }: { label: string; value: number; unit?: string }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {unit && <div className="stat-unit">{unit}</div>}
    </div>
  )
}

function ActionButton({ icon, children, onClick }: { icon: React.ReactNode; children: React.ReactNode; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        width: '100%',
        padding: '0.875rem 1rem',
        background: 'var(--color-elevated)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: '10px',
        color: 'var(--text-primary)',
        cursor: 'pointer',
        transition: 'all 150ms ease',
        textAlign: 'left',
      }}
      onMouseOver={e => {
        e.currentTarget.style.borderColor = 'var(--color-violet)'
        e.currentTarget.style.background = 'var(--color-border)'
      }}
      onMouseOut={e => {
        e.currentTarget.style.borderColor = 'var(--color-border-subtle)'
        e.currentTarget.style.background = 'var(--color-elevated)'
      }}
    >
      <span style={{
        width: 36,
        height: 36,
        borderRadius: 10,
        background: 'var(--color-violet-glow)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--color-violet)'
      }}>
        {icon}
      </span>
      <span style={{ fontWeight: 500 }}>{children}</span>
    </button>
  )
}

function ChecklistItem({ done, children }: { done: boolean; children: React.ReactNode }) {
  return (
    <div className={`checklist-item ${done ? 'done' : ''}`}>
      <div className="checklist-check">
        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M2 6l3 3 5-5" />
        </svg>
      </div>
      <span className="checklist-text">{children}</span>
    </div>
  )
}

function SpeakerCard({
  speaker,
  onTrain,
}: {
  speaker: Speaker
  onTrain?: (speaker: Speaker) => void
}) {
  return (
    <div className="speaker-card">
      <div className={`avatar ${speaker.is_primary ? 'avatar-primary' : ''}`}>
        {speaker.name.charAt(0).toUpperCase()}
      </div>
      <div className="speaker-info">
        <div className="speaker-name">{speaker.name}</div>
        <div className="speaker-meta">
          {speaker.is_primary ? 'Primary speaker' : `${speaker.sample_count} samples`}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
        {speaker.is_primary && (
          <span className="speaker-badge speaker-badge-primary">Primary</span>
        )}
        {onTrain && (
          <button
            className="btn btn-ghost btn-icon"
            onClick={(e) => {
              e.stopPropagation()
              onTrain(speaker)
            }}
            title="Train voice"
            style={{ padding: '0.375rem' }}
          >
            <WaveformIcon />
          </button>
        )}
      </div>
    </div>
  )
}

function ConversationItem({
  conversation,
  onClick,
  loading
}: {
  conversation: Conversation
  onClick?: () => void
  loading?: boolean
}) {
  const date = new Date(conversation.start_time)
  const formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const isProcessing = conversation.status === 'processing' || conversation.duration_seconds === null

  return (
    <div className="conversation-item" onClick={onClick} style={{ opacity: loading ? 0.6 : 1 }}>
      <div className="conversation-speakers">
        <div className="avatar">
          {conversation.speakers?.[0]?.name?.charAt(0)?.toUpperCase() || '?'}
        </div>
      </div>
      <div className="conversation-meta">
        <div className="conversation-title">{conversation.title || 'Untitled conversation'}</div>
        <div className="conversation-info">
          <span>{formattedDate}</span>
          <span>·</span>
          <span>{conversation.capture_method}</span>
        </div>
      </div>
      {isProcessing ? (
        <span className="processing-badge">
          <span className="dot" />
          Processing
        </span>
      ) : (
        <span className="conversation-duration">
          {Math.round((conversation.duration_seconds || 0) / 60)} min
        </span>
      )}
      <ChevronRightIcon />
    </div>
  )
}

function EmptyState({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <div className="empty-state">
      <div className="empty-state-icon">{icon}</div>
      <div className="empty-state-title">{title}</div>
      <div className="empty-state-text">{text}</div>
    </div>
  )
}

function SearchResultCard({
  result,
  onClick,
}: {
  result: SearchResult
  onClick: () => void
}) {
  const date = result.start_time
    ? new Date(result.start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Unknown date'

  const relevancePercent = Math.round(result.relevance_score * 100)
  const relevanceColor = relevancePercent >= 80 ? 'var(--color-emerald)' : relevancePercent >= 60 ? 'var(--color-amber)' : 'var(--color-rose)'

  return (
    <div
      onClick={onClick}
      style={{
        padding: '1rem',
        background: 'var(--color-elevated)',
        borderRadius: 12,
        border: '1px solid var(--color-border-subtle)',
        cursor: 'pointer',
        transition: 'all 150ms ease',
      }}
      onMouseOver={e => {
        e.currentTarget.style.borderColor = 'var(--color-violet)'
        e.currentTarget.style.background = 'var(--color-surface)'
      }}
      onMouseOut={e => {
        e.currentTarget.style.borderColor = 'var(--color-border-subtle)'
        e.currentTarget.style.background = 'var(--color-elevated)'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
        <div>
          <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
            {result.title || 'Untitled conversation'}
          </div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
            {date} · {result.duration_seconds ? `${Math.round(result.duration_seconds / 60)} min` : 'Duration unknown'}
          </div>
        </div>
        <div style={{
          fontSize: '0.75rem',
          fontWeight: 500,
          padding: '0.25rem 0.5rem',
          borderRadius: 6,
          background: `${relevanceColor}20`,
          color: relevanceColor,
        }}>
          {relevancePercent}% match
        </div>
      </div>

      {/* Matched segments preview */}
      {result.matched_segments.length > 0 && (
        <div style={{
          fontSize: '0.875rem',
          color: 'var(--text-secondary)',
          padding: '0.75rem',
          background: 'var(--color-surface)',
          borderRadius: 8,
          maxHeight: '120px',
          overflow: 'hidden',
        }}>
          {result.matched_segments.slice(0, 2).map((seg, idx) => (
            <div key={seg.id} style={{ marginBottom: idx < 1 ? '0.5rem' : 0 }}>
              <span style={{ color: 'var(--text-dim)', fontStyle: 'italic' }}>
                "...{seg.text.length > 150 ? seg.text.slice(0, 150) + '...' : seg.text}"
              </span>
            </div>
          ))}
          {result.matched_segments.length > 2 && (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
              +{result.matched_segments.length - 2} more matches
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Modal
// ============================================================================

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <CloseIcon />
        </button>
        <h2 className="modal-title">{title}</h2>
        {children}
      </div>
    </div>
  )
}

// ============================================================================
// Conversation Detail View
// ============================================================================

function ConversationDetailView({
  conversation,
  speakers,
  onBack,
  onRefresh,
}: {
  conversation: Conversation
  speakers: Speaker[]
  onBack: () => void
  onRefresh: () => void
}) {
  const { getToken } = useAuth()
  const [unknownSpeakerModal, setUnknownSpeakerModal] = useState<{
    label: string
    segmentIds: string[]
  } | null>(null)
  const [reassignModal, setReassignModal] = useState<{
    segment: Segment
    currentSpeaker: ConversationSpeaker | null
  } | null>(null)
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const date = conversation.start_time
    ? new Date(conversation.start_time).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Unknown date'

  const duration = conversation.duration_seconds
    ? `${Math.round(conversation.duration_seconds / 60)} minutes`
    : 'Duration unknown'

  // Build speaker map for quick lookup
  const speakerMap = new Map<string, ConversationSpeaker>()
  conversation.speakers?.forEach(s => speakerMap.set(s.id, s))

  // Group segments by unknown speaker labels for potential registration
  const unknownSpeakerGroups = new Map<string, Segment[]>()
  let unknownCounter = 0
  const segmentUnknownLabels = new Map<string, string>()

  conversation.segments?.forEach(seg => {
    if (!seg.speaker_id) {
      // Group consecutive unknown segments (simplified - could be improved)
      const label = `Unknown Speaker ${++unknownCounter}`
      segmentUnknownLabels.set(seg.id, label)
      if (!unknownSpeakerGroups.has(label)) {
        unknownSpeakerGroups.set(label, [])
      }
      unknownSpeakerGroups.get(label)!.push(seg)
    }
  })

  function handleUnknownSpeakerClick(label: string, segments: Segment[]) {
    setUnknownSpeakerModal({
      label,
      segmentIds: segments.map(s => s.id),
    })
  }

  async function handleRegisterNewSpeaker(name: string) {
    if (!unknownSpeakerModal) return
    try {
      const token = await getToken()
      const res = await fetch(`${API_BASE}/v1/speakers/from-segments`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          segment_ids: unknownSpeakerModal.segmentIds,
        }),
      })
      if (res.ok) {
        setUnknownSpeakerModal(null)
        setNotification({ type: 'success', message: `${name} has been registered as a new speaker` })
        setTimeout(() => setNotification(null), 3000)
        onRefresh()
      } else {
        throw new Error('Failed to register speaker')
      }
    } catch {
      setNotification({ type: 'error', message: 'Failed to register speaker' })
      setTimeout(() => setNotification(null), 3000)
    }
  }

  async function handleMergeIntoSpeaker(speakerId: string) {
    if (!unknownSpeakerModal) return
    try {
      const token = await getToken()
      const res = await fetch(`${API_BASE}/v1/speakers/${speakerId}/merge-segments`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          segment_ids: unknownSpeakerModal.segmentIds,
        }),
      })
      if (res.ok) {
        setUnknownSpeakerModal(null)
        const speaker = speakers.find(s => s.id === speakerId)
        setNotification({
          type: 'success',
          message: `Segments assigned to ${speaker?.name || 'speaker'}`,
        })
        setTimeout(() => setNotification(null), 3000)
        onRefresh()
      } else {
        throw new Error('Failed to merge segments')
      }
    } catch {
      setNotification({ type: 'error', message: 'Failed to merge segments' })
      setTimeout(() => setNotification(null), 3000)
    }
  }

  async function handleReassignSegment(speakerId: string) {
    if (!reassignModal) return
    try {
      const token = await getToken()
      const res = await fetch(`${API_BASE}/v1/review/segment/${reassignModal.segment.id}/reassign`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          speaker_id: speakerId,
        }),
      })
      if (res.ok) {
        setReassignModal(null)
        const speaker = speakers.find(s => s.id === speakerId)
        setNotification({
          type: 'success',
          message: `Segment reassigned to ${speaker?.name || 'speaker'}`,
        })
        setTimeout(() => setNotification(null), 3000)
        onRefresh()
      } else {
        throw new Error('Failed to reassign segment')
      }
    } catch {
      setNotification({ type: 'error', message: 'Failed to reassign segment' })
      setTimeout(() => setNotification(null), 3000)
    }
  }

  function handleSegmentClick(segment: Segment, speaker: ConversationSpeaker | null) {
    if (!segment.speaker_id) {
      // Unknown speaker - use the existing unknown speaker flow
      const unknownLabel = segmentUnknownLabels.get(segment.id)
      if (unknownLabel) {
        const segs = unknownSpeakerGroups.get(unknownLabel)
        if (segs) handleUnknownSpeakerClick(unknownLabel, segs)
      }
    } else {
      // Known speaker - open reassignment modal
      setReassignModal({ segment, currentSpeaker: speaker })
    }
  }

  return (
    <>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <button
          onClick={onBack}
          className="btn btn-ghost"
          style={{ marginBottom: '1rem', padding: '0.5rem 0' }}
        >
          <ArrowLeftIcon /> Back to conversations
        </button>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 600, marginBottom: '0.5rem' }}>
          {conversation.title || 'Untitled conversation'}
        </h1>
        <div style={{ color: 'var(--text-muted)', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <span>{date}</span>
          <span>·</span>
          <span>{duration}</span>
          <span>·</span>
          <span style={{ textTransform: 'capitalize' }}>{conversation.capture_method}</span>
        </div>
      </div>

      {/* Speaker Legend */}
      {conversation.speakers && conversation.speakers.length > 0 && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 className="section-title" style={{ marginBottom: '1rem' }}>Speakers</h3>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            {conversation.speakers.map((speaker, index) => (
              <SpeakerLegendItem key={speaker.id} speaker={speaker} colorIndex={index} />
            ))}
          </div>
        </div>
      )}

      {/* Summary */}
      {conversation.summary && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 className="section-title" style={{ marginBottom: '0.75rem' }}>Summary</h3>
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>{conversation.summary}</p>
        </div>
      )}

      {/* Topics & Action Items */}
      {(conversation.topics?.length || conversation.action_items?.length) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          {conversation.topics && conversation.topics.length > 0 && (
            <div className="card">
              <h3 className="section-title" style={{ marginBottom: '0.75rem' }}>Topics</h3>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {conversation.topics.map((topic, i) => (
                  <span key={i} className="topic-badge">{topic}</span>
                ))}
              </div>
            </div>
          )}
          {conversation.action_items && conversation.action_items.length > 0 && (
            <div className="card">
              <h3 className="section-title" style={{ marginBottom: '0.75rem' }}>Action Items</h3>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', color: 'var(--text-secondary)' }}>
                {conversation.action_items.map((item, i) => (
                  <li key={i} style={{ marginBottom: '0.25rem' }}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Transcript */}
      <div className="card">
        <h3 className="section-title" style={{ marginBottom: '1rem' }}>Transcript</h3>
        {conversation.segments && conversation.segments.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {conversation.segments.map((segment) => {
              const speaker = segment.speaker_id ? speakerMap.get(segment.speaker_id) ?? null : null
              const speakerIndex = speaker
                ? conversation.speakers?.findIndex(s => s.id === speaker.id) ?? 0
                : -1
              const unknownLabel = segmentUnknownLabels.get(segment.id)

              return (
                <TranscriptSegment
                  key={segment.id}
                  segment={segment}
                  speaker={speaker}
                  speakerColorIndex={speakerIndex}
                  unknownLabel={unknownLabel}
                  onClick={() => handleSegmentClick(segment, speaker)}
                />
              )
            })}
          </div>
        ) : (
          <EmptyState
            icon={<MicIcon />}
            title="No transcript yet"
            text="The transcript will appear here once processing is complete"
          />
        )}
      </div>

      {/* Notification */}
      {notification && (
        <div
          className={`toast toast-${notification.type}`}
          style={{ position: 'fixed', bottom: '2rem', right: '2rem', zIndex: 300 }}
        >
          {notification.type === 'success' ? <CheckIcon /> : <AlertIcon />}
          <span>{notification.message}</span>
        </div>
      )}

      {/* Unknown Speaker Modal */}
      {unknownSpeakerModal && (
        <UnknownSpeakerModal
          label={unknownSpeakerModal.label}
          segmentCount={unknownSpeakerModal.segmentIds.length}
          existingSpeakers={speakers}
          onClose={() => setUnknownSpeakerModal(null)}
          onRegisterNew={handleRegisterNewSpeaker}
          onMergeInto={handleMergeIntoSpeaker}
        />
      )}

      {/* Reassign Speaker Modal */}
      {reassignModal && (
        <ReassignSpeakerModal
          currentSpeaker={reassignModal.currentSpeaker}
          segmentText={reassignModal.segment.text}
          existingSpeakers={speakers}
          onClose={() => setReassignModal(null)}
          onReassign={handleReassignSegment}
        />
      )}
    </>
  )
}

function SpeakerLegendItem({
  speaker,
  colorIndex,
}: {
  speaker: ConversationSpeaker
  colorIndex: number
}) {
  const colors = ['var(--color-violet)', 'var(--color-cyan)', 'var(--color-emerald)', 'var(--color-amber)', 'var(--color-rose)']
  const color = colors[colorIndex % colors.length]

  const confidenceLevel = speaker.confidence
    ? speaker.confidence >= 0.8 ? 'high' : speaker.confidence >= 0.6 ? 'medium' : 'low'
    : null

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <div
        style={{
          width: 12,
          height: 12,
          borderRadius: 4,
          background: color,
        }}
      />
      <span style={{ fontWeight: 500 }}>{speaker.name}</span>
      {speaker.is_primary && (
        <span className="speaker-badge speaker-badge-primary">You</span>
      )}
      {confidenceLevel && (
        <ConfidenceBadge level={confidenceLevel} value={speaker.confidence!} />
      )}
    </div>
  )
}

function TranscriptSegment({
  segment,
  speaker,
  speakerColorIndex,
  unknownLabel,
  onClick,
}: {
  segment: Segment
  speaker: ConversationSpeaker | null
  speakerColorIndex: number
  unknownLabel?: string
  onClick?: () => void
}) {
  const colors = ['var(--color-violet)', 'var(--color-cyan)', 'var(--color-emerald)', 'var(--color-amber)', 'var(--color-rose)']
  const color = speakerColorIndex >= 0 ? colors[speakerColorIndex % colors.length] : 'var(--text-muted)'

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000)
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const confidenceLevel = segment.confidence
    ? segment.confidence >= 0.8 ? 'high' : segment.confidence >= 0.6 ? 'medium' : 'low'
    : null

  const isUnknown = !segment.speaker_id

  return (
    <div
      className="transcript-segment"
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <div
        className="transcript-speaker-bar"
        style={{ background: color }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
          <span style={{ fontWeight: 600, color: color }}>
            {speaker?.name || unknownLabel || 'Unknown'}
          </span>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
            {formatTime(segment.start_offset_ms)}
          </span>
          {confidenceLevel && (
            <ConfidenceBadge level={confidenceLevel} value={segment.confidence!} />
          )}
          {!segment.is_verified && (
            <span className="needs-review-badge">Needs Review</span>
          )}
          <span style={{
            fontSize: '0.6875rem',
            color: 'var(--color-violet)',
            marginLeft: 'auto',
            opacity: 0.7,
          }}>
            {isUnknown ? 'Click to identify' : 'Click to change'}
          </span>
        </div>
        <p style={{ margin: 0, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          {segment.text}
        </p>
      </div>
    </div>
  )
}

function ConfidenceBadge({ level, value }: { level: 'high' | 'medium' | 'low'; value: number }) {
  const styles: Record<string, { bg: string; color: string }> = {
    high: { bg: 'rgba(16, 185, 129, 0.15)', color: 'var(--color-emerald)' },
    medium: { bg: 'rgba(245, 158, 11, 0.15)', color: 'var(--color-amber)' },
    low: { bg: 'rgba(244, 63, 94, 0.15)', color: 'var(--color-rose)' },
  }
  const style = styles[level]

  return (
    <span
      style={{
        fontSize: '0.6875rem',
        padding: '0.125rem 0.375rem',
        borderRadius: 4,
        background: style.bg,
        color: style.color,
        fontWeight: 500,
      }}
    >
      {Math.round(value * 100)}%
    </span>
  )
}

function UnknownSpeakerModal({
  label,
  segmentCount,
  existingSpeakers,
  onClose,
  onRegisterNew,
  onMergeInto,
}: {
  label: string
  segmentCount: number
  existingSpeakers: Speaker[]
  onClose: () => void
  onRegisterNew: (name: string) => void
  onMergeInto: (speakerId: string) => void
}) {
  const [mode, setMode] = useState<'choose' | 'register'>('choose')
  const [newName, setNewName] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleRegister() {
    if (!newName.trim()) return
    setLoading(true)
    await onRegisterNew(newName.trim())
    setLoading(false)
  }

  async function handleMerge(speakerId: string) {
    setLoading(true)
    await onMergeInto(speakerId)
    setLoading(false)
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <CloseIcon />
        </button>
        <h2 className="modal-title">Identify {label}</h2>
        <p style={{ textAlign: 'center', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
          {segmentCount} segment{segmentCount > 1 ? 's' : ''} from this speaker
        </p>

        {mode === 'choose' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {/* Register New Option */}
              <button
                className="speaker-option"
                onClick={() => setMode('register')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem',
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border-subtle)',
                  borderRadius: 12,
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left',
                }}
              >
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: 'var(--color-violet-glow)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--color-violet)',
                }}>
                  <UserPlusIcon />
                </div>
                <div>
                  <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                    Register as New Speaker
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                    This is someone new
                  </div>
                </div>
              </button>

              {/* Existing Speakers */}
              {existingSpeakers.length > 0 && (
                <>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Or assign to existing speaker
                  </div>
                  {existingSpeakers.map(speaker => (
                    <button
                      key={speaker.id}
                      className="speaker-option"
                      onClick={() => handleMerge(speaker.id)}
                      disabled={loading}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '1rem',
                        background: 'var(--color-surface)',
                        border: '1px solid var(--color-border-subtle)',
                        borderRadius: 12,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        width: '100%',
                        textAlign: 'left',
                        opacity: loading ? 0.6 : 1,
                      }}
                    >
                      <div className={`avatar ${speaker.is_primary ? 'avatar-primary' : ''}`}>
                        {speaker.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                          {speaker.name}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                          {speaker.is_primary ? 'Primary speaker (You)' : `${speaker.sample_count} samples`}
                        </div>
                      </div>
                    </button>
                  ))}
                </>
              )}
            </div>
          </>
        )}

        {mode === 'register' && (
          <>
            <button
              onClick={() => setMode('choose')}
              className="btn btn-ghost"
              style={{ marginBottom: '1rem', padding: '0.5rem 0' }}
            >
              <ArrowLeftIcon /> Back
            </button>
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="input-label">Speaker Name</label>
              <input
                type="text"
                className="input"
                placeholder="e.g., Sophie, Dr. Smith, etc."
                value={newName}
                onChange={e => setNewName(e.target.value)}
                autoFocus
              />
            </div>
            <button
              className="btn btn-primary btn-lg"
              style={{ width: '100%' }}
              disabled={!newName.trim() || loading}
              onClick={handleRegister}
            >
              {loading ? 'Registering...' : 'Register Speaker'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

function ReassignSpeakerModal({
  currentSpeaker,
  segmentText,
  existingSpeakers,
  onClose,
  onReassign,
}: {
  currentSpeaker: ConversationSpeaker | null
  segmentText: string
  existingSpeakers: Speaker[]
  onClose: () => void
  onReassign: (speakerId: string) => void
}) {
  const [loading, setLoading] = useState(false)

  async function handleReassign(speakerId: string) {
    setLoading(true)
    await onReassign(speakerId)
    setLoading(false)
  }

  // Filter out current speaker from options
  const otherSpeakers = existingSpeakers.filter(s => s.id !== currentSpeaker?.id)

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <CloseIcon />
        </button>
        <h2 className="modal-title">Reassign Speaker</h2>

        {/* Current assignment */}
        <div style={{
          padding: '1rem',
          background: 'var(--color-surface)',
          borderRadius: 12,
          marginBottom: '1rem',
          border: '1px solid var(--color-border-subtle)'
        }}>
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
            Currently assigned to:
          </div>
          <div style={{ fontWeight: 500 }}>
            {currentSpeaker?.name || 'Unknown'}
            {currentSpeaker?.is_primary && (
              <span className="speaker-badge speaker-badge-primary" style={{ marginLeft: '0.5rem' }}>You</span>
            )}
          </div>
        </div>

        {/* Segment preview */}
        <div style={{
          padding: '0.75rem',
          background: 'var(--color-elevated)',
          borderRadius: 8,
          marginBottom: '1.5rem',
          fontSize: '0.875rem',
          color: 'var(--text-secondary)',
          fontStyle: 'italic',
          maxHeight: '80px',
          overflow: 'hidden',
        }}>
          "{segmentText.length > 150 ? segmentText.slice(0, 150) + '...' : segmentText}"
        </div>

        <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Reassign to:
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {otherSpeakers.map(speaker => (
            <button
              key={speaker.id}
              onClick={() => handleReassign(speaker.id)}
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem',
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border-subtle)',
                borderRadius: 12,
                cursor: loading ? 'not-allowed' : 'pointer',
                width: '100%',
                textAlign: 'left',
                opacity: loading ? 0.6 : 1,
              }}
            >
              <div className={`avatar ${speaker.is_primary ? 'avatar-primary' : ''}`}>
                {speaker.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                  {speaker.name}
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                  {speaker.is_primary ? 'Primary speaker (You)' : `${speaker.sample_count} samples`}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Audio Recorder Panel
// ============================================================================

function AudioRecorderPanel({ onComplete }: { onComplete: () => void }) {
  const { getToken } = useAuth()
  const [state, setState] = useState<'idle' | 'recording' | 'preview' | 'uploading'>('idle')
  const [duration, setDuration] = useState(0)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const mediaRecorderRef = { current: null as MediaRecorder | null }
  const chunksRef = { current: [] as Blob[] }
  const streamRef = { current: null as MediaStream | null }
  const timerRef = { current: null as ReturnType<typeof setInterval> | null }

  async function startRecording() {
    setError(null)
    chunksRef.current = []

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true }
      })
      streamRef.current = stream

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus' : 'audio/webm'

      const recorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = recorder

      recorder.ondataavailable = e => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType })
        setAudioUrl(URL.createObjectURL(blob))
        setState('preview')
      }

      recorder.start(1000)
      setState('recording')

      timerRef.current = setInterval(() => {
        setDuration(d => d + 1)
      }, 1000)
    } catch (err) {
      setError('Could not access microphone')
    }
  }

  function stopRecording() {
    if (timerRef.current) clearInterval(timerRef.current)
    if (mediaRecorderRef.current?.state !== 'inactive') {
      mediaRecorderRef.current?.stop()
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
    }
  }

  async function uploadRecording() {
    if (chunksRef.current.length === 0) return
    setState('uploading')

    try {
      const token = await getToken()
      const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm'
      const blob = new Blob(chunksRef.current, { type: mimeType })
      const formData = new FormData()
      formData.append('file', blob, `recording_${Date.now()}.webm`)

      const res = await fetch(`${API_BASE}/v1/audio/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      })

      if (!res.ok) throw new Error('Upload failed')
      onComplete()
    } catch {
      setError('Upload failed. Please try again.')
      setState('preview')
    }
  }

  function reset() {
    if (audioUrl) URL.revokeObjectURL(audioUrl)
    setAudioUrl(null)
    setDuration(0)
    setState('idle')
  }

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`

  return (
    <div style={{ textAlign: 'center' }}>
      {error && (
        <div className="toast toast-error" style={{ marginBottom: '1rem', justifyContent: 'center' }}>
          {error}
        </div>
      )}

      {/* Visualizer */}
      <div className="recorder-visualizer">
        {Array.from({ length: 32 }).map((_, i) => (
          <div
            key={i}
            className={`recorder-bar ${state === 'recording' ? 'active' : ''}`}
            style={{
              height: state === 'recording' ? `${20 + Math.random() * 60}%` : '20%',
              animationDelay: `${i * 0.05}s`
            }}
          />
        ))}
      </div>

      {/* Timer */}
      <div className="recorder-timer">{formatTime(duration)}</div>
      <div className="recorder-timer-label">
        {state === 'idle' && 'Ready to record'}
        {state === 'recording' && 'Recording...'}
        {state === 'preview' && 'Review your recording'}
        {state === 'uploading' && 'Uploading...'}
      </div>

      {/* Audio preview */}
      {audioUrl && state === 'preview' && (
        <div style={{ margin: '1.5rem 0' }}>
          <audio controls src={audioUrl} style={{ width: '100%', borderRadius: '8px' }} />
        </div>
      )}

      {/* Controls */}
      <div className="recorder-controls">
        {state === 'idle' && (
          <button className="btn-record" onClick={startRecording}>
            <MicIcon />
          </button>
        )}
        {state === 'recording' && (
          <button className="btn-record recording" onClick={stopRecording}>
            <StopIcon />
          </button>
        )}
        {state === 'preview' && (
          <>
            <button className="btn btn-secondary" onClick={reset}>Re-record</button>
            <button className="btn btn-primary" onClick={uploadRecording}>Upload</button>
          </>
        )}
        {state === 'uploading' && (
          <button className="btn btn-primary" disabled>
            <div style={{ width: 16, height: 16, border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            Uploading...
          </button>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Audio Uploader
// ============================================================================

function AudioUploader({ onComplete }: { onComplete: () => void }) {
  const { getToken } = useAuth()
  const [dragActive, setDragActive] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleFile(file: File) {
    if (!file.type.startsWith('audio/')) {
      setError('Please select an audio file')
      return
    }

    setUploading(true)
    setError(null)

    try {
      const token = await getToken()
      const formData = new FormData()
      formData.append('file', file)

      const res = await fetch(`${API_BASE}/v1/audio/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      })

      if (!res.ok) throw new Error('Upload failed')
      onComplete()
    } catch {
      setError('Upload failed. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div
      className={`upload-zone ${dragActive ? 'active' : ''}`}
      onDragOver={e => { e.preventDefault(); setDragActive(true) }}
      onDragLeave={() => setDragActive(false)}
      onDrop={e => {
        e.preventDefault()
        setDragActive(false)
        const file = e.dataTransfer.files[0]
        if (file) handleFile(file)
      }}
    >
      {uploading ? (
        <div style={{ color: 'var(--text-muted)' }}>
          <div style={{ width: 32, height: 32, border: '3px solid var(--color-violet)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
          Uploading...
        </div>
      ) : (
        <>
          <div className="upload-zone-icon"><UploadIcon /></div>
          <p className="upload-zone-text">Drag and drop your audio file here</p>
          <p className="upload-zone-hint">or click to browse</p>
          <input
            type="file"
            accept="audio/*"
            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
            onChange={e => {
              const file = e.target.files?.[0]
              if (file) handleFile(file)
            }}
          />
          {error && <p style={{ color: 'var(--color-rose)', marginTop: '1rem', fontSize: '0.875rem' }}>{error}</p>}
        </>
      )}
    </div>
  )
}

// ============================================================================
// Speaker Enroller
// ============================================================================

function SpeakerEnroller({ onComplete }: { onComplete: () => void }) {
  const { getToken } = useAuth()
  const [name, setName] = useState('')
  const [state, setState] = useState<'form' | 'recording' | 'uploading' | 'done'>('form')
  const [duration, setDuration] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const mediaRecorderRef = { current: null as MediaRecorder | null }
  const chunksRef = { current: [] as Blob[] }
  const streamRef = { current: null as MediaStream | null }
  const timerRef = { current: null as ReturnType<typeof setInterval> | null }

  const MIN_DURATION = 30

  async function startRecording() {
    setError(null)
    chunksRef.current = []

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true }
      })
      streamRef.current = stream

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus' : 'audio/webm'

      const recorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = recorder

      recorder.ondataavailable = e => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }

      recorder.onstop = () => uploadSample()

      recorder.start(1000)
      setState('recording')

      timerRef.current = setInterval(() => {
        setDuration(d => d + 1)
      }, 1000)
    } catch {
      setError('Could not access microphone')
    }
  }

  function stopRecording() {
    if (timerRef.current) clearInterval(timerRef.current)
    if (mediaRecorderRef.current?.state !== 'inactive') {
      mediaRecorderRef.current?.stop()
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
    }
  }

  async function uploadSample() {
    if (chunksRef.current.length === 0) return
    setState('uploading')

    try {
      const token = await getToken()
      const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm'
      const blob = new Blob(chunksRef.current, { type: mimeType })

      const formData = new FormData()
      formData.append('audio', blob, 'enrollment.webm')
      formData.append('name', name)
      formData.append('is_primary', 'false')

      const res = await fetch(`${API_BASE}/v1/speakers/enroll`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      })

      if (!res.ok) throw new Error('Enrollment failed')
      setState('done')
      setTimeout(onComplete, 1500)
    } catch {
      setError('Enrollment failed. Please try again.')
      setState('form')
    }
  }

  const progress = Math.min((duration / MIN_DURATION) * 100, 100)
  const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`

  if (state === 'done') {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div className="onboarding-icon onboarding-icon-success" style={{ margin: '0 auto 1.5rem' }}>
          <CheckIcon />
        </div>
        <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>Speaker Enrolled!</h3>
        <p style={{ color: 'var(--text-muted)' }}>{name} has been added to your speakers.</p>
      </div>
    )
  }

  return (
    <div>
      {error && (
        <div className="toast toast-error" style={{ marginBottom: '1rem' }}>{error}</div>
      )}

      {state === 'form' && (
        <>
          <div style={{ marginBottom: '1.5rem' }}>
            <label className="input-label">Speaker Name</label>
            <input
              type="text"
              className="input"
              placeholder="e.g., Sophie, Mom, Dr. Smith"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
            />
          </div>
          <button
            className="btn btn-primary btn-lg"
            style={{ width: '100%' }}
            disabled={!name.trim()}
            onClick={startRecording}
          >
            Start Recording Voice Sample
          </button>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: '1rem' }}>
            You'll need to speak for at least 30 seconds
          </p>
        </>
      )}

      {(state === 'recording' || state === 'uploading') && (
        <>
          <div className="progress-container">
            <div className="progress-label">
              <span className="progress-label-text">Recording progress</span>
              <span className="progress-label-value">{formatTime(duration)} / 0:30</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <div className="recorder-visualizer" style={{ height: 80 }}>
            {Array.from({ length: 24 }).map((_, i) => (
              <div
                key={i}
                className={`recorder-bar ${state === 'recording' ? 'active' : ''}`}
                style={{ animationDelay: `${i * 0.05}s` }}
              />
            ))}
          </div>

          <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
            {duration < MIN_DURATION
              ? `Keep speaking for ${MIN_DURATION - duration} more seconds...`
              : 'You can stop recording now'}
          </p>

          <button
            className="btn btn-primary btn-lg"
            style={{ width: '100%' }}
            disabled={duration < MIN_DURATION || state === 'uploading'}
            onClick={stopRecording}
          >
            {state === 'uploading' ? 'Processing...' : 'Stop & Save'}
          </button>
        </>
      )}
    </div>
  )
}

// ============================================================================
// Speaker Trainer (for improving voice recognition)
// ============================================================================

function SpeakerTrainer({ speaker, onComplete }: { speaker: Speaker; onComplete: () => void }) {
  const { getToken } = useAuth()
  const [mode, setMode] = useState<'choose' | 'upload' | 'record'>('choose')
  const [state, setState] = useState<'idle' | 'recording' | 'uploading' | 'done'>('idle')
  const [duration, setDuration] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [dragActive, setDragActive] = useState(false)

  const mediaRecorderRef = { current: null as MediaRecorder | null }
  const chunksRef = { current: [] as Blob[] }
  const streamRef = { current: null as MediaStream | null }
  const timerRef = { current: null as ReturnType<typeof setInterval> | null }

  const MIN_DURATION = 10

  async function handleFileUpload(file: File) {
    if (!file.type.startsWith('audio/')) {
      setError('Please select an audio file')
      return
    }

    setState('uploading')
    setError(null)

    try {
      const token = await getToken()
      const formData = new FormData()
      formData.append('audio', file)

      const res = await fetch(`${API_BASE}/v1/speakers/${speaker.id}/train`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || 'Training failed')
      }

      setState('done')
      setTimeout(onComplete, 1500)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Training failed. Please try again.')
      setState('idle')
    }
  }

  async function startRecording() {
    setError(null)
    chunksRef.current = []

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true }
      })
      streamRef.current = stream

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus' : 'audio/webm'

      const recorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = recorder

      recorder.ondataavailable = e => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }

      recorder.onstop = () => uploadRecording()

      recorder.start(1000)
      setState('recording')

      timerRef.current = setInterval(() => {
        setDuration(d => d + 1)
      }, 1000)
    } catch {
      setError('Could not access microphone')
    }
  }

  function stopRecording() {
    if (timerRef.current) clearInterval(timerRef.current)
    if (mediaRecorderRef.current?.state !== 'inactive') {
      mediaRecorderRef.current?.stop()
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
    }
  }

  async function uploadRecording() {
    if (chunksRef.current.length === 0) return
    setState('uploading')

    try {
      const token = await getToken()
      const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm'
      const blob = new Blob(chunksRef.current, { type: mimeType })

      const formData = new FormData()
      formData.append('audio', blob, 'training.webm')

      const res = await fetch(`${API_BASE}/v1/speakers/${speaker.id}/train`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || 'Training failed')
      }

      setState('done')
      setTimeout(onComplete, 1500)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Training failed. Please try again.')
      setState('idle')
      setMode('choose')
    }
  }

  const progress = Math.min((duration / MIN_DURATION) * 100, 100)
  const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`

  if (state === 'done') {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div className="onboarding-icon onboarding-icon-success" style={{ margin: '0 auto 1.5rem' }}>
          <CheckIcon />
        </div>
        <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>Training Complete!</h3>
        <p style={{ color: 'var(--text-muted)' }}>
          {speaker.name}'s voice profile has been improved.
        </p>
      </div>
    )
  }

  return (
    <div>
      {error && (
        <div className="toast toast-error" style={{ marginBottom: '1rem' }}>{error}</div>
      )}

      <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', textAlign: 'center' }}>
        Provide a high-quality audio sample of {speaker.name} speaking to improve voice recognition accuracy.
      </p>

      {mode === 'choose' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <button
            onClick={() => setMode('upload')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1rem',
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border-subtle)',
              borderRadius: 12,
              cursor: 'pointer',
              width: '100%',
              textAlign: 'left',
            }}
          >
            <div style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: 'var(--color-violet-glow)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-violet)',
            }}>
              <UploadIcon />
            </div>
            <div>
              <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>Upload Audio File</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                Use an existing recording (recommended)
              </div>
            </div>
          </button>

          <button
            onClick={() => { setMode('record'); startRecording() }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1rem',
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border-subtle)',
              borderRadius: 12,
              cursor: 'pointer',
              width: '100%',
              textAlign: 'left',
            }}
          >
            <div style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: 'var(--color-violet-glow)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-violet)',
            }}>
              <MicIcon />
            </div>
            <div>
              <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>Record Now</div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                Record using your microphone
              </div>
            </div>
          </button>
        </div>
      )}

      {mode === 'upload' && state === 'idle' && (
        <>
          <button
            onClick={() => setMode('choose')}
            className="btn btn-ghost"
            style={{ marginBottom: '1rem', padding: '0.5rem 0' }}
          >
            <ArrowLeftIcon /> Back
          </button>
          <div
            className={`upload-zone ${dragActive ? 'active' : ''}`}
            onDragOver={e => { e.preventDefault(); setDragActive(true) }}
            onDragLeave={() => setDragActive(false)}
            onDrop={e => {
              e.preventDefault()
              setDragActive(false)
              const file = e.dataTransfer.files[0]
              if (file) handleFileUpload(file)
            }}
          >
            <div className="upload-zone-icon"><UploadIcon /></div>
            <p className="upload-zone-text">Drag and drop an audio file</p>
            <p className="upload-zone-hint">WAV, MP3, M4A • 10+ seconds of speech</p>
            <input
              type="file"
              accept="audio/*"
              style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
              onChange={e => {
                const file = e.target.files?.[0]
                if (file) handleFileUpload(file)
              }}
            />
          </div>
        </>
      )}

      {mode === 'upload' && state === 'uploading' && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{
            width: 48,
            height: 48,
            border: '3px solid var(--color-violet)',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1.5rem'
          }} />
          <p style={{ color: 'var(--text-muted)' }}>Processing audio...</p>
        </div>
      )}

      {mode === 'record' && (state === 'recording' || state === 'uploading') && (
        <>
          <div className="progress-container">
            <div className="progress-label">
              <span className="progress-label-text">Recording progress</span>
              <span className="progress-label-value">{formatTime(duration)} / 0:10</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <div className="recorder-visualizer" style={{ height: 80 }}>
            {Array.from({ length: 24 }).map((_, i) => (
              <div
                key={i}
                className={`recorder-bar ${state === 'recording' ? 'active' : ''}`}
                style={{ animationDelay: `${i * 0.05}s` }}
              />
            ))}
          </div>

          <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
            {duration < MIN_DURATION
              ? `Keep speaking for ${MIN_DURATION - duration} more seconds...`
              : 'You can stop recording now'}
          </p>

          <button
            className="btn btn-primary btn-lg"
            style={{ width: '100%' }}
            disabled={duration < MIN_DURATION || state === 'uploading'}
            onClick={stopRecording}
          >
            {state === 'uploading' ? 'Processing...' : 'Stop & Train'}
          </button>
        </>
      )}
    </div>
  )
}

// ============================================================================
// Icons
// ============================================================================

function Waveform() {
  return (
    <div className="waveform">
      {[1,2,3,4,5].map(i => <div key={i} className="waveform-bar" />)}
    </div>
  )
}

function MicIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" x2="12" y1="19" y2="22" />
    </svg>
  )
}

function UsersIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  )
}

function BotIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="18" height="10" x="3" y="11" rx="2" />
      <circle cx="12" cy="5" r="2" />
      <path d="M12 7v4" />
      <line x1="8" x2="8" y1="16" y2="16" />
      <line x1="16" x2="16" y1="16" y2="16" />
    </svg>
  )
}

function UploadIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" x2="12" y1="3" y2="15" />
    </svg>
  )
}

function UserPlusIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="19" x2="19" y1="8" y2="14" />
      <line x1="22" x2="16" y1="11" y2="11" />
    </svg>
  )
}

function KeyIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m21 2-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0 3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

function AlertIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" x2="12" y1="8" y2="12" />
      <line x1="12" x2="12.01" y1="16" y2="16" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" x2="6" y1="6" y2="18" />
      <line x1="6" x2="18" y1="6" y2="18" />
    </svg>
  )
}

function StopIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
  )
}

function ArrowLeftIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  )
}

function ChevronRightIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-dim)', marginLeft: 'auto' }}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}

function WaveformIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 10v4" />
      <path d="M6 6v12" />
      <path d="M10 3v18" />
      <path d="M14 8v8" />
      <path d="M18 5v14" />
      <path d="M22 10v4" />
    </svg>
  )
}

function SearchIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  )
}
